import {createSlice, Dispatch, PayloadAction} from '@reduxjs/toolkit';
import type {PersistConfig} from 'redux-persist'
import {persistReducer} from 'redux-persist'
import persistStorage from 'redux-persist/lib/storage'
import type {IMySongsPageStore, MySongsPageFilterLessonFeatureType, MySongsPageMenuType} from "./mySongsPage.types";
import {initialMySongsPageState} from "./mySongsPage.store-initial";
import {
  IToggleFilterDifficultyCategoryPayload,
  selectFilterDifficultyCategoryIdCaseReducer,
  unselectFilterDifficultyCategoryIdCaseReducer
} from "./reducers/filters/toggle-filter-difficulty-category-id-reducer";
import type {GetState} from "../../../../redux/rootReducer";
import {
  updateSongDetailsLessonUserLibraryAndFavoriteReducer
} from "./reducers/song-details/update-song-details-lesson-user-library-and-favorite-reducer";
import {store} from "../../../../redux/store";
import {
  IFilterDifficultyIdReducerPayload,
  selectFilterDifficultyIdCaseReducer,
  unselectFilterDifficultyIdReducer
} from "./reducers/filters/toggle-filter-difficulty-id-reducer";
import {
  mySongsPageDefaultSortConfig,
  MySongsPageSortColumnType,
  MySongsPageSortDirectionType, mySongsPageSorters,
  songsFilterSortConfig
} from "../../../../config/my-songs-page-sort";
import {ISetSongDetailsRequestStatusPayload} from "./reducers/song-details/set-song-details-request-status-reducer";
import {ISongDetailsResponseSong} from "../../../../client/models/songs/ISongDetailsResponse";
import {mySongsFiltersStore} from "../my-songs-filters-store/mySongsFiltersStore";

const mySongsPageSlice = createSlice({
  name: `mySongsPage`,
  initialState: initialMySongsPageState,

  reducers: (create) => ({
    // Active Menu
    removeActiveMenuReducer: create.reducer((store: IMySongsPageStore, action: PayloadAction<MySongsPageMenuType>) => {
      store.activeMenu = store.activeMenu?.filter((menuName) => menuName !== action.payload);
    }),
    toggleSingleActiveMenuReducer: create.reducer((store: IMySongsPageStore, action: PayloadAction<MySongsPageMenuType>) => {
      const menuName = action.payload;
      const isMenuAlreadyActive = store.activeMenu?.includes(menuName);

      store.activeMenu = isMenuAlreadyActive
        ? store.activeMenu?.filter((menu) => menu !== menuName)
        : [menuName];
    }),
    toggleActiveMenuReducer: create.reducer((store: IMySongsPageStore, action: PayloadAction<MySongsPageMenuType>) => {
      const menuName = action.payload;
      const isMenuAlreadyActive = store.activeMenu?.includes(menuName);

      store.activeMenu = isMenuAlreadyActive
        ? store.activeMenu?.filter((menu) => menu !== menuName)
        : [...(store.activeMenu ?? []), menuName];
    }),

    // Filters
    toggleFilterGenreIdReducer: create.reducer((store: IMySongsPageStore, action: PayloadAction<number>) => {
      const genreId: number = action.payload;
      const isGenreIdAlreadySelected: boolean = !!store.genres?.includes(genreId);

      store.genres = isGenreIdAlreadySelected
        ? store.genres?.filter((storeGenreId) => storeGenreId !== genreId)
        : [...(store.genres ?? []), genreId];
    }),
    resetFilterGenresReducer: create.reducer((store: IMySongsPageStore) => { store.genres = initialMySongsPageState.genres; }),
    toggleFilterDifficultyCategoryIdReducer: create.reducer((store: IMySongsPageStore, action: PayloadAction<IToggleFilterDifficultyCategoryPayload>) => {
      const isAlreadySelected: boolean = !!store.difficulty?.difficultyCategories?.includes(action.payload.difficultyCategoryId);
      if (isAlreadySelected) {
        return unselectFilterDifficultyCategoryIdCaseReducer(store, action);
      }
      return selectFilterDifficultyCategoryIdCaseReducer(store, action);
    }),
    toggleFilterDifficultyIdReducer: create.reducer((store: IMySongsPageStore, action: PayloadAction<IFilterDifficultyIdReducerPayload>) => {
      const isAlreadySelected: boolean = (store.difficulty?.difficulties ?? []).includes(action.payload.difficultyId);
      if (isAlreadySelected) {
        return unselectFilterDifficultyIdReducer(store, action);
      }
      return selectFilterDifficultyIdCaseReducer(store, action);
    }),
    resetFilterDifficultyReducer: create.reducer((store: IMySongsPageStore) => { store.difficulty = initialMySongsPageState.difficulty; }),
    setFilterFeatureReducer: create.reducer((store: IMySongsPageStore, action: PayloadAction<MySongsPageFilterLessonFeatureType>) => { store.features = action.payload; }),
    toggleFilterArrangementTypeIdReducer: create.reducer((store: IMySongsPageStore, action: PayloadAction<number>) => {
      const arrangementTypeId: number = action.payload;
      const isAlreadySelected: boolean = !!store.arrangementTypes?.includes(arrangementTypeId);

      store.arrangementTypes = isAlreadySelected
        ? store.arrangementTypes?.filter((storeArrangementTypeId) => storeArrangementTypeId !== arrangementTypeId)
        : [...(store.arrangementTypes ?? []), arrangementTypeId];
    }),
    resetFilterArrangementTypesReducer: create.reducer((store: IMySongsPageStore) => { store.arrangementTypes = initialMySongsPageState.arrangementTypes; }),
    setSearchFocusReducer: create.reducer((store: IMySongsPageStore, action: PayloadAction<boolean>) => {
      store.search = {
        ...store.search,
        isFocused: action.payload,
      };
    }),
    setSearchValueReducer: create.reducer((store: IMySongsPageStore, action: PayloadAction<string | undefined>) => {
      store.search = {
        ...store.search,
        value: action.payload,
      };
    }),
    resetSearchReducer: create.reducer((store) => { store.search = initialMySongsPageState.search; }),
    resetFiltersKeepingTheActiveMenuTheSearchAndTheSelectedSongReducer: create.reducer((store) => {
      return {
        // start with everything using initialState
        ...initialMySongsPageState,
        // keep any active menu
        activeMenu: [
          ...(store.activeMenu ?? []),
        ],
        // keep current search
        search: {
          ...(store.search ?? {}),
        },
        // keep current selected song
        selectedSong: {
          ...store.selectedSong ?? {},
        },
      };
    }),

    // Sorters
    setSortColumnReducer: create.reducer((store: IMySongsPageStore, action: PayloadAction<MySongsPageSortColumnType>) => {
      const selectedColumn = action.payload;
      const sortColumnOption = songsFilterSortConfig.options.find(({column}) => column === selectedColumn);

      store.sort = {
        ...store.sort,
        column: selectedColumn,
        direction: sortColumnOption?.defaultDirection ?? undefined,
      };
    }),
    setSortDirectionReducer: create.reducer((store: IMySongsPageStore, action: PayloadAction<MySongsPageSortDirectionType>) => {
      store.sort = {
        ...store.sort,
        direction: action.payload,
      }
    }),

    // Song Details
    selectSongDetailsByIdReducer: create.reducer((store: IMySongsPageStore, action: PayloadAction<number>) => {
      const songId = action.payload;

      if (store.selectedSong?.id !== songId) {
        store.selectedSong = {id: songId};
      }
    }),
    setSongDetailsRequestStatusReducer: create.reducer((store: IMySongsPageStore, action: PayloadAction<ISetSongDetailsRequestStatusPayload>) => {
      const {songId, status} = action.payload;
      if (store.selectedSong?.id === songId) {
        store.selectedSong = {
          ...store.selectedSong ?? {},
          requestStatus: status,
        };
      }
    }),
    loadSongDetailsReducer: create.reducer((store: IMySongsPageStore, action: PayloadAction<ISongDetailsResponseSong>) => {
      const songDetails = action.payload;
      if (store.selectedSong?.id === songDetails.id) {
        store.selectedSong = {
          ...store.selectedSong,
          details: songDetails,
        };
      }
    }),
    clearSongDetailsReducer: create.reducer((store: IMySongsPageStore) => { store.selectedSong = initialMySongsPageState.selectedSong; }),
    updateSongDetailsLessonUserLibraryAndFavoriteReducer: create.reducer(updateSongDetailsLessonUserLibraryAndFavoriteReducer),
    setSongDetailsPreviewModalContentReducer: create.reducer((store: IMySongsPageStore, action: PayloadAction<'video' | 'sheetMusic'>) => {
      store.selectedSong = {
        ...store.selectedSong,
        previewModalContent: action.payload,
      };
    }),
    closeSongDetailsPreviewModalReducer: create.reducer((store: IMySongsPageStore) => {
      store.selectedSong = {
        ...store.selectedSong,
        previewModalContent: undefined,
      };
    }),
  }),

  extraReducers: (builder) => {
    builder.addCase("@@router/LOCATION_CHANGE", (store: IMySongsPageStore) => {
      return {
        ...store,
        activeMenu: initialMySongsPageState.activeMenu,
      };
    });
  },
  selectors: {
    isMenuActiveSelector: (store: IMySongsPageStore) => (menu: MySongsPageMenuType) => store.activeMenu?.includes(menu),
    isFilteringSelector: (store: IMySongsPageStore) => {
      return !!store.genres?.length
        || !!store.difficulty?.difficultyCategories?.length
        || !!store.difficulty?.difficulties?.length
        || !!store.arrangementTypes?.length
        || store?.features === "lesson.hasVideo"
    },
    getSortColumnSelector: (store: IMySongsPageStore): MySongsPageSortColumnType => getSortColumn(store),
    getSortDirectionSelector: (store: IMySongsPageStore): MySongsPageSortDirectionType => {
      return store.sort?.direction ??
        // fall back to column default direction
        mySongsPageSorters.get(getSortColumn(store))?.defaultDirection ??
        // fall back to default direction
        mySongsPageDefaultSortConfig.direction;
    },
  },
});

const getSortColumn = (store: IMySongsPageStore): MySongsPageSortColumnType =>
  store.sort?.column ?? mySongsPageDefaultSortConfig.column;

// Exported for tests
export const {
  toggleFilterGenreIdReducer,
  toggleFilterDifficultyCategoryIdReducer,
  toggleFilterDifficultyIdReducer,
  resetFilterDifficultyReducer,
  toggleFilterArrangementTypeIdReducer,
} = mySongsPageSlice.actions;

const toggleFilterDifficultyCategoryById = (difficultyCategoryId: number) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const childDifficultyIds: number[] = mySongsFiltersStore.getDifficultyIdsFromDifficultyCategoryId(difficultyCategoryId);
    dispatch(toggleFilterDifficultyCategoryIdReducer({difficultyCategoryId, difficultyIds: childDifficultyIds}));
  };
};

const toggleFilterDifficultyById = (difficultyId: number) => {
  return (dispatch: Dispatch, getState: GetState) => {
    const difficultyCategoryId: number = mySongsFiltersStore.getDifficultyById(difficultyId)?.categoryId ?? 0;
    const childDifficultyIds: number[] = mySongsFiltersStore.getDifficultyIdsFromDifficultyCategoryId(difficultyCategoryId);
    dispatch(toggleFilterDifficultyIdReducer({difficultyId, difficultyCategory: {difficultyCategoryId, difficultyIds: childDifficultyIds} }));
  };
};

export const resetAndCloseSearchAction = () => {
  return (dispatch: Dispatch, getState: GetState) => {
    mySongsPageStore.resetSearch();
    const isMobileSearchMenuOpen = getState().mySongsPage.activeMenu?.includes('mobile.search');
    if (isMobileSearchMenuOpen) {
      mySongsPageStore.removeActiveMenu('mobile.search');
    }
  }
};

const mySongsPagePersistConfig: PersistConfig<IMySongsPageStore> = {
  key: `mySongsPage`,
  storage: persistStorage,
  whitelist: [
    `sort`,
    `genres`,
    `difficulty`,
    `features`,
    `arrangementTypes`,
    `search`,
  ],
};

export const mySongsPageBaseReducer = mySongsPageSlice.reducer;
export const mySongsPageReducer = persistReducer<IMySongsPageStore>(mySongsPagePersistConfig, mySongsPageBaseReducer);

export const mySongsPageStore = {
  // Setters - things that modify state
  // Setters: Active Menu
  removeActiveMenu: (menuName: MySongsPageMenuType) => { store.dispatch(mySongsPageSlice.actions.removeActiveMenuReducer(menuName)); },
  toggleSingleActiveMenu: (menuName: MySongsPageMenuType) => { store.dispatch(mySongsPageSlice.actions.toggleSingleActiveMenuReducer(menuName)); },
  toggleActiveMenu: (menuName: MySongsPageMenuType) => { store.dispatch(mySongsPageSlice.actions.toggleActiveMenuReducer(menuName)); },
  // Setters: Filters
  toggleFilterGenreId: (genreId: number) => { store.dispatch(mySongsPageSlice.actions.toggleFilterGenreIdReducer(genreId)); },
  resetFilterGenres: () => { store.dispatch(mySongsPageSlice.actions.resetFilterGenresReducer()); },
  toggleFilterDifficultyCategoryById: (difficultyCategoryId: number) => { store.dispatch(toggleFilterDifficultyCategoryById(difficultyCategoryId)); },
  toggleFilterDifficultyById: (difficultyId: number) => { store.dispatch(toggleFilterDifficultyById(difficultyId)); },
  resetFilterDifficulty: () => { store.dispatch(mySongsPageSlice.actions.resetFilterDifficultyReducer()); },
  setFilterFeature: (feature: MySongsPageFilterLessonFeatureType) => { store.dispatch(mySongsPageSlice.actions.setFilterFeatureReducer(feature)); },
  toggleFilterArrangementTypeId: (arrangementTypeId: number) => { store.dispatch(mySongsPageSlice.actions.toggleFilterArrangementTypeIdReducer(arrangementTypeId)); },
  resetFilterArrangementTypes: () => { store.dispatch(mySongsPageSlice.actions.resetFilterArrangementTypesReducer()); },
  setSearchFocus: (isFocused: boolean) => { store.dispatch(mySongsPageSlice.actions.setSearchFocusReducer(isFocused)); },
  setSearchValue: (value: string | undefined) => { store.dispatch(mySongsPageSlice.actions.setSearchValueReducer(value)); },
  resetSearch: () => { store.dispatch(mySongsPageSlice.actions.resetSearchReducer()); },
  resetFiltersKeepingTheActiveMenuTheSearchAndTheSelectedSong: () => { store.dispatch(mySongsPageSlice.actions.resetFiltersKeepingTheActiveMenuTheSearchAndTheSelectedSongReducer()); },
  // Setters: Sorters
  setSortColumn: (column: MySongsPageSortColumnType) => { store.dispatch(mySongsPageSlice.actions.setSortColumnReducer(column)); },
  setSortDirection: (direction: MySongsPageSortDirectionType) => { store.dispatch(mySongsPageSlice.actions.setSortDirectionReducer(direction)); },
  // Setters: Song Details
  selectSongDetailsById: (songId: number) => { store.dispatch(mySongsPageSlice.actions.selectSongDetailsByIdReducer(songId)); },
  setSongDetailsRequestStatus: (songId: number, status: ISetSongDetailsRequestStatusPayload['status']) => { store.dispatch(mySongsPageSlice.actions.setSongDetailsRequestStatusReducer({songId, status})); },
  loadSongDetails: (songDetails: ISongDetailsResponseSong) => { store.dispatch(mySongsPageSlice.actions.loadSongDetailsReducer(songDetails)); },
  clearSongDetails: () => { store.dispatch(mySongsPageSlice.actions.clearSongDetailsReducer()); },
  updateSongDetailsLessonUserLibraryAndFavorite: (lessonId: number, isInLibrary: boolean, isFavorited: boolean) => {store.dispatch(mySongsPageSlice.actions.updateSongDetailsLessonUserLibraryAndFavoriteReducer({lessonId, isInLibrary, isFavorited}));},
  setSongDetailsPreviewModalContent: (content: 'video' | 'sheetMusic') => { store.dispatch(mySongsPageSlice.actions.setSongDetailsPreviewModalContentReducer(content)); },
  closeSongDetailsPreviewModal: () => { store.dispatch(mySongsPageSlice.actions.closeSongDetailsPreviewModalReducer()); },

  // Getters
  // Getters: Active Menu
  isMenuActive: (menu: MySongsPageMenuType) => mySongsPageSlice.selectors.isMenuActiveSelector(store.getState())(menu),
  // Getters: Filters
  isFiltering: () => mySongsPageSlice.selectors.isFilteringSelector(store.getState()),
  // Getters: Sorters
  getSortColumn: () => mySongsPageSlice.selectors.getSortColumnSelector(store.getState()),
  getSortDirection: () => mySongsPageSlice.selectors.getSortDirectionSelector(store.getState()),
};
